import React, { useCallback, useMemo, useRef } from 'react';
import { Button, GridItem, Heading, Image, useToast } from '@chakra-ui/react';
import { useMatches, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { faUpload } from '@fortawesome/free-solid-svg-icons';
import PlaceholderText from '../text/PlaceholderText';
import noModelsPlaceholder from '../../assets/img/no-models-placeholder.svg';
import noSheetsPlaceholder from '../../assets/img/no-sheets-placeholder.svg';
import SparkelIcon from './icon/SparkelIcon';
import { useViewerMode, ViewerMode } from './viewer-mode';
import { useSheetViewer } from './SheetViewer';
import { useSheets } from 'src/hooks/sheets';

type NoDataPlaceholderProps = {
  hasModels: boolean;
  loaded: boolean;
};

type ProjectPageHandle = {
  hideViewer?: boolean;
};

export default function NoDataPlaceholder({
  hasModels,
  loaded,
}: NoDataPlaceholderProps): React.ReactElement | null {
  const { viewerMode } = useViewerMode();
  const { activeSheetId } = useSheetViewer();
  const { t } = useTranslation('project');
  const { projectId } = useParams<{ projectId: string }>();
  const { createSheet } = useSheets(projectId ?? '');
  const { changeActiveSheet } = useSheetViewer();
  const sheetInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const matches = useMatches();
  const shouldHideViewer = useMemo(
    () => matches.some((m) => (m.handle as ProjectPageHandle)?.hideViewer),
    [matches]
  );

  const handleSheetInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files) return;
      void Promise.all(
        [...e.target.files].map(async (file) => {
          try {
            const created = await createSheet(file, file.name, 1);
            if (created?.id) {
              changeActiveSheet(created.id);
            }
            toast({
              status: 'success',
              duration: 3000,
              title: t('toast.success.sheet-upload'),
            });
          } catch {
            toast({
              status: 'error',
              duration: 3000,
              title: t('toast.error.sheet-upload'),
            });
          }
        })
      );
    },
    [createSheet, changeActiveSheet, toast, t]
  );

  // A slight hack, but it'll work for now, since since we "auto-select" the first sheet
  const hasSheets = activeSheetId !== null;

  // Show nothing if we're still loading models or sheets
  if (!loaded || shouldHideViewer) {
    return null;
  }

  if (!hasModels && viewerMode === ViewerMode.Models) {
    return (
      <GridItem
        textAlign={'center'}
        zIndex={0}
        area="sidebar-left-end / sidebar-left-end / sidebar-right-start / sidebar-right-start"
        display="flex"
        flexDirection={'column'}
        justifyContent="flex-start"
        alignItems="center"
        alignSelf="start"
        paddingTop={'25%'}
        sx={{
          '@media (max-width: 1200px)': {
            paddingRight: 2,
            gridArea:
              'sidebar-left-start / sidebar-left-end / sidebar-right-end / sidebar-right-end',
          },
        }}
      >
        <Image
          alt="model placeholder"
          objectFit={'contain'}
          src={noModelsPlaceholder}
          maxHeight={100}
        />
        <Heading color={'gray.500'} size={'lg'} as="h3">
          {t('viewer.no-models-placeholder')}
        </Heading>
        <PlaceholderText>{t('viewer.no-models-description')}</PlaceholderText>
      </GridItem>
    );
  } else if (!hasSheets && viewerMode === ViewerMode.Sheets) {
    return (
      <GridItem
        textAlign={'center'}
        zIndex={0}
        area="sidebar-left-end / sidebar-left-end / sidebar-right-start / sidebar-right-start"
        display="flex"
        flexDirection={'column'}
        justifyContent="flex-start"
        alignItems="center"
        alignSelf="start"
        paddingTop={'15%'}
        sx={{
          '@media (max-width: 1200px)': {
            paddingRight: 2,
            gridArea:
              'sidebar-left-start / sidebar-left-end / sidebar-right-end / sidebar-right-end',
          },
        }}
      >
        <Image
          alt="model placeholder"
          objectFit={'contain'}
          src={noSheetsPlaceholder}
          maxHeight={300}
        />
        <Heading color={'gray.500'} size={'lg'} as="h3">
          {t('viewer.no-sheets-placeholder')}
        </Heading>
        <PlaceholderText>{t('viewer.no-sheets-description')}</PlaceholderText>
        <input
          ref={sheetInputRef}
          type="file"
          multiple
          accept="application/pdf"
          onChange={handleSheetInputChange}
          style={{ display: 'none' }}
        />
        <Button
          mt={4}
          colorScheme="brand"
          leftIcon={<SparkelIcon icon={faUpload} color="inherit" />}
          onClick={() => sheetInputRef.current?.click()}
        >
          {t('files.add-sheet')}
        </Button>
      </GridItem>
    );
  } else {
    return null;
  }
}
